"use client";

import { useState, useMemo } from "react";
import type { WeekPressure, FeederWeight, CountryHolidays } from "@/lib/conditions/busyness-data";
import {
  weekRawPressure,
  compressPressure,
  countryRegionCounts,
  pressureColour,
  pressureLabel,
} from "@/lib/conditions/busyness-pressure";

type Props = {
  weeks: WeekPressure[];
  holidays: CountryHolidays[];
  locationList: { slug: string; name: string; country: string; type: "ski" | "surf" | "sailing" }[];
  feederWeightsBySlug: Record<string, FeederWeight[]>;
  publicHolidaysByCountry: Record<string, string[]>;
  seasonStart: string;
  seasonEnd: string;
};

function formatWeekLabel(weekStart: string): string {
  const d = new Date(weekStart + "T00:00:00Z");
  return `w/c ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })}`;
}

type CountryPressure = {
  country: string;
  pressure: number;
};

function computeCountryPressures(
  selectedWeek: WeekPressure,
  locationList: { slug: string; country: string }[],
  feederWeightsBySlug: Record<string, FeederWeight[]>,
  holidays: CountryHolidays[],
  publicHolidaysByCountry: Record<string, string[]>,
  seasonStart: string,
  seasonEnd: string,
): CountryPressure[] {
  const countryToSlugs = new Map<string, string[]>();
  for (const loc of locationList) {
    const slugs = countryToSlugs.get(loc.country) ?? [];
    slugs.push(loc.slug);
    countryToSlugs.set(loc.country, slugs);
  }

  // Same model and constants as the heatbar (shared busyness-pressure module),
  // averaged across each destination country's resorts.
  const bars = holidays.flatMap((ch) => ch.holidays);
  const regionCounts = countryRegionCounts(bars);

  const results: CountryPressure[] = [];

  for (const [country, slugs] of countryToSlugs) {
    let totalPressure = 0;
    let count = 0;

    for (const slug of slugs) {
      const weights = feederWeightsBySlug[slug];
      if (!weights?.length) continue;

      const weightMap = new Map(weights.map((w) => [w.countryCode, w.weight]));
      const raw = weekRawPressure(selectedWeek.weekStart, selectedWeek.weekEnd, {
        bars,
        weightFor: (cc) => weightMap.get(cc) ?? 0,
        publicHolidaysByCountry,
        seasonStart,
        seasonEnd,
        regionCounts,
      });

      totalPressure += compressPressure(raw);
      count++;
    }

    if (count > 0) {
      results.push({ country, pressure: totalPressure / count });
    }
  }

  results.sort((a, b) => b.pressure - a.pressure);
  return results;
}

export default function CountryRanking({
  weeks,
  holidays,
  locationList,
  feederWeightsBySlug,
  publicHolidaysByCountry,
  seasonStart,
  seasonEnd,
}: Props) {
  const [selectedWeekStart, setSelectedWeekStart] = useState<string>("");

  const selectedWeek = useMemo(() => {
    if (!selectedWeekStart) return null;
    return weeks.find((w) => w.weekStart === selectedWeekStart) ?? null;
  }, [weeks, selectedWeekStart]);

  const rankings = useMemo(() => {
    if (!selectedWeek) return [];
    return computeCountryPressures(
      selectedWeek,
      locationList,
      feederWeightsBySlug,
      holidays,
      publicHolidaysByCountry,
      seasonStart,
      seasonEnd,
    );
  }, [selectedWeek, locationList, feederWeightsBySlug, holidays, publicHolidaysByCountry, seasonStart, seasonEnd]);

  if (weeks.length === 0) return null;

  return (
    <div>
      <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-1">
        By week
      </h2>
      <p className="text-[13px] text-text-muted mb-5">
        Pick a week to see which destination countries are busiest, ranked by crowd pressure.
      </p>

      <div className="mb-5">
        <label htmlFor="week-ranking-selector" className="sr-only">
          Select week
        </label>
        <select
          id="week-ranking-selector"
          value={selectedWeekStart}
          onChange={(e) => setSelectedWeekStart(e.target.value)}
          className="w-full md:w-56 px-4 py-2.5 rounded-[10px] border border-border-light bg-white text-[13px] text-navy focus:outline-none focus:border-gold/40 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20d%3D%22M3%205l3%203%203-3%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20fill%3D%22none%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat pr-8"
        >
          <option value="">Choose a week...</option>
          {weeks.map((w) => (
            <option key={w.weekStart} value={w.weekStart}>
              {formatWeekLabel(w.weekStart)}
            </option>
          ))}
        </select>
      </div>

      {selectedWeek && rankings.length > 0 && (
        <div className="bg-white rounded-[14px] border border-border-light overflow-hidden">
          {/* Desktop table */}
          <table className="w-full text-[13px] hidden md:table">
            <thead>
              <tr className="border-b border-border-light">
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[1px] text-text-muted font-semibold w-12">
                  #
                </th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[1px] text-text-muted font-semibold">
                  Country
                </th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[1px] text-text-muted font-semibold">
                  Crowd pressure
                </th>
                <th className="text-right px-5 py-3 text-[11px] uppercase tracking-[1px] text-text-muted font-semibold w-28">
                  Level
                </th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((entry, i) => {
                const pct = Math.round(entry.pressure * 100);
                return (
                  <tr key={entry.country} className="border-b border-border-light last:border-b-0">
                    <td className="px-5 py-3">
                      <span className="font-serif text-[17px] font-bold text-navy tabular-nums">{i + 1}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="font-serif text-[15px] font-semibold text-navy">{entry.country}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[200px] h-2.5 rounded-full bg-warm-bg overflow-hidden">
                          <div
                            className={`h-full rounded-full ${pressureColour(entry.pressure)}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="tabular-nums font-semibold text-[14px] text-navy w-10 text-right">{pct}%</span>
                      </div>
                    </td>
                    <td className="text-right px-5 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold text-white ${pressureColour(entry.pressure)}`}>
                        {pressureLabel(entry.pressure)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border-light">
            {rankings.map((entry, i) => {
              const pct = Math.round(entry.pressure * 100);
              return (
                <div key={entry.country} className="px-5 py-4 flex items-center gap-4">
                  <span className="font-serif text-[20px] font-bold text-navy tabular-nums shrink-0 w-7">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-[15px] font-semibold text-navy mb-1.5">{entry.country}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-warm-bg overflow-hidden">
                        <div
                          className={`h-full rounded-full ${pressureColour(entry.pressure)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="tabular-nums font-semibold text-[13px] text-navy shrink-0">{pct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedWeek && rankings.length === 0 && (
        <div className="bg-white rounded-[14px] border border-border-light p-8 text-center">
          <p className="text-[14px] text-text-muted">No destination data available for this week.</p>
        </div>
      )}
    </div>
  );
}
